import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sparkle, X as XIcon, PaperPlaneRight, Lightning } from "@phosphor-icons/react";

/**
 * BKflow Coach — floating AI assistant available on every screen.
 * Backed by /api/ai/chat which uses Claude with the app's default norm-aware
 * system prompt (NF C 15-100, KNX, Loxone, Daikin, etc.).
 *
 * UX: press the floating pill → panel slides up with quick chips + free-form
 * question. Answers are streamed back and rendered inline. Everything is
 * ephemeral per browser session (fresh session_id on open).
 */

const CHIPS = [
  { label: "Section câble", prompt: "Quelle section de câble je dois prévoir pour un circuit " },
  { label: "Code erreur", prompt: "Explique-moi le code erreur " },
  { label: "Volume SDB", prompt: "Rappelle-moi les volumes de sécurité dans une salle de bain (NF C 15-100) et le matériel autorisé dans chaque zone." },
  { label: "Calibre disjoncteur", prompt: "Quel calibre de disjoncteur pour " },
  { label: "Différentiel type", prompt: "Quel type de différentiel (A/AC/F) pour " },
  { label: "Norme domotique", prompt: "Bonnes pratiques KNX/Loxone pour " },
];

export default function FloatingCoach() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', text }
  const [draft, setDraft] = useState("");
  const [sessionId] = useState(() => `coach-${Date.now()}-${Math.floor(Math.random() * 9999)}`);
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || thinking) return;
    setMessages((m) => [...m, { role: "user", text: clean }]);
    setDraft("");
    setThinking(true);
    try {
      // Non-streamed reply — /api/ai/chat streams SSE but this endpoint also
      // supports blocking calls; we let axios buffer the whole SSE payload.
      const r = await api.post("/ai/chat", { session_id: sessionId, message: clean });
      // Normalise both stream+SSE and structured responses.
      let reply = "";
      if (typeof r.data === "string") {
        // SSE payload — extract the concatenated text deltas.
        reply = r.data
          .split("\n")
          .filter((l) => l.startsWith("data: "))
          .map((l) => l.slice(6))
          .filter((l) => l && l !== "[DONE]")
          .map((l) => { try { const j = JSON.parse(l); return j.content || j.text || ""; } catch { return l; } })
          .join("");
      } else if (r.data?.reply) {
        reply = r.data.reply;
      } else if (r.data?.messages?.length) {
        reply = r.data.messages[r.data.messages.length - 1]?.content || "";
      } else {
        reply = JSON.stringify(r.data);
      }
      if (!reply.trim()) reply = "…";
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setThinking(false); }
  };

  const onChipClick = (chip) => {
    setDraft(chip.prompt);
    setTimeout(() => inputRef.current?.focus(), 30);
    // Auto-send only for self-contained prompts (ending with a period).
    if (chip.prompt.trim().endsWith(".") || chip.prompt.trim().endsWith("?")) send(chip.prompt);
  };

  return (
    <>
      {/* Floating pill — always visible except on the dedicated AI page */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-24 md:bottom-6 right-4 md:right-6 w-14 h-14 rounded-full bg-gradient-to-br from-[#005CFF] to-purple-600 border border-[#7CB2FF]/30 shadow-lg shadow-[#005CFF]/40 flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-all"
          title="Coach BKflow — Assistant norme électrique"
          data-testid="floating-coach-btn"
        >
          <Sparkle size={22} weight="fill" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-[#0A0A0A] animate-pulse" />
        </button>
      )}

      {/* Slide-up panel */}
      {open && (
        <div
          className="fixed z-50 inset-x-2 bottom-2 md:inset-auto md:right-6 md:bottom-6 md:w-[420px] md:max-w-[92vw] max-h-[85vh] md:max-h-[75vh] rounded-2xl bg-[#0A0A0A] border border-white/10 shadow-2xl shadow-black/60 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
          data-testid="floating-coach-panel"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-gradient-to-r from-[#005CFF]/15 to-purple-500/10">
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 active:scale-95 transition-all"
              title="Fermer"
              data-testid="floating-coach-close"
            >
              <XIcon size={14} weight="bold" />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#7CB2FF]" />
              <div>
                <div className="text-sm font-semibold leading-none">Coach BKflow</div>
                <div className="text-[9px] text-white/50 mt-0.5">NF C 15-100 · KNX · Loxone · Daikin</div>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={() => setMessages([])}
                className="text-[10px] text-white/50 hover:text-white/80 px-2 py-1"
                title="Nouvelle question"
                data-testid="floating-coach-reset"
              >
                Vider
              </button>
            )}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="floating-coach-messages">
            {messages.length === 0 && !thinking && (
              <div className="text-center py-6 space-y-3">
                <div className="text-xs text-white/50">
                  Pose ta question technique ou choisis un raccourci.
                </div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {CHIPS.map((c) => (
                    <button
                      key={c.label}
                      onClick={() => onChipClick(c)}
                      className="px-2.5 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-[11px] hover:bg-[#005CFF]/20 hover:border-[#005CFF]/40 hover:text-white active:scale-95 transition-all flex items-center gap-1"
                      data-testid={`coach-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Lightning size={9} weight="fill" className="text-amber-400" />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={`msg-${i}-${m.role}-${(m.text || "").slice(0, 20)}`}
                className={`rounded-xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#005CFF]/25 border border-[#005CFF]/40 ml-6"
                    : "bg-white/[0.04] border border-white/10 mr-4"
                }`}
              >
                {m.text}
              </div>
            ))}
            {thinking && (
              <div className="rounded-xl px-3 py-2 bg-white/[0.04] border border-white/10 mr-4 flex items-center gap-2 text-xs text-white/60">
                <div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                Coach réfléchit…
              </div>
            )}
          </div>

          <div className="p-2.5 border-t border-white/10 bg-white/[0.02]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(draft); } }}
                placeholder="Ex : section pour four 32A à 25m ?"
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-xs resize-none focus:outline-none focus:border-[#005CFF]/50 max-h-24"
                data-testid="floating-coach-input"
              />
              <button
                onClick={() => send(draft)}
                disabled={!draft.trim() || thinking}
                className="w-9 h-9 rounded-full bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-40 flex items-center justify-center text-white active:scale-95 transition-all"
                title="Envoyer"
                data-testid="floating-coach-send"
              >
                <PaperPlaneRight size={13} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
